import { supabase, TABLES } from '../config/supabase'

// 用户服务
export const userService = {
  // 获取当前用户
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  // 创建用户资料
  async createUserProfile(userId, profile) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .insert({
        id: userId,
        ...profile
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  // 获取用户资料
  async getUserProfile(userId) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .select('*')
      .eq('id', userId)
      .single()

    if (error) throw error
    return data
  },

  // 更新用户资料
  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return data
  }
}

// 音乐曲目服务
export const trackService = {
  // 保存音乐曲目到数据库
  async saveTrack(track) {
    const { data, error } = await supabase
      .from(TABLES.TRACKS)
      .upsert({
        id: track.id,
        title: track.title,
        artist: track.artist,
        album: track.album,
        duration: track.duration,
        audio_url: track.audioUrl,
        artwork: track.artwork,
        download_url: track.downloadUrl,
        source: track.source || 'unknown',
        license: track.license,
        tags: track.tags || [],
        metadata: {
          originalData: track
        }
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  // 批量保存音乐曲目
  async saveTracks(tracks) {
    const tracksData = tracks.map(track => ({
      id: track.id,
      title: track.title,
      artist: track.artist,
      album: track.album,
      duration: track.duration,
      audio_url: track.audioUrl,
      artwork: track.artwork,
      download_url: track.downloadUrl,
      source: track.source || 'unknown',
      license: track.license,
      tags: track.tags || [],
      metadata: {
        originalData: track
      }
    }))

    const { data, error } = await supabase
      .from(TABLES.TRACKS)
      .upsert(tracksData)
      .select()

    if (error) throw error
    return data
  },

  // 根据ID获取曲目
  async getTrack(trackId) {
    const { data, error } = await supabase
      .from(TABLES.TRACKS)
      .select('*')
      .eq('id', trackId)
      .single()

    if (error) throw error
    return data
  },

  // 搜索曲目
  async searchTracks(query, limit = 20) {
    const { data, error } = await supabase
      .from(TABLES.TRACKS)
      .select('*')
      .or(`title.ilike.%${query}%,artist.ilike.%${query}%,album.ilike.%${query}%`)
      .limit(limit)

    if (error) throw error
    return data
  }
}

// 收藏服务
export const favoriteService = {
  // 获取用户收藏
  async getUserFavorites(userId) {
    if (!userId) return []

    const { data, error } = await supabase
      .from(TABLES.USER_FAVORITES)
      .select(`
        *,
        tracks (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data?.map(fav => ({
      ...fav.tracks,
      audioUrl: fav.tracks.audio_url,
      downloadUrl: fav.tracks.download_url
    })) || []
  },

  // 添加到收藏
  async addToFavorites(userId, trackId) {
    if (!userId) return null

    const { data, error } = await supabase
      .from(TABLES.USER_FAVORITES)
      .insert({
        user_id: userId,
        track_id: trackId
      })
      .select()
      .single()

    if (error) {
      // 如果是重复收藏错误，忽略
      if (error.code === '23505') return null
      throw error
    }
    return data
  },

  // 从收藏中移除
  async removeFromFavorites(userId, trackId) {
    if (!userId) return

    const { error } = await supabase
      .from(TABLES.USER_FAVORITES)
      .delete()
      .eq('user_id', userId)
      .eq('track_id', trackId)

    if (error) throw error
  },

  // 检查是否已收藏
  async isFavorite(userId, trackId) {
    if (!userId) return false

    const { data, error } = await supabase
      .from(TABLES.USER_FAVORITES)
      .select('id')
      .eq('user_id', userId)
      .eq('track_id', trackId)
      .single()

    return !error && data !== null
  }
}

// 推荐服务
export const recommendationService = {
  // 保存推荐记录
  async saveRecommendation(userId, recommendation) {
    const { data, error } = await supabase
      .from(TABLES.RECOMMENDATIONS)
      .insert({
        user_id: userId,
        mood: recommendation.mood,
        weather: recommendation.weather,
        location: recommendation.location,
        tracks: recommendation.tracks,
        ai_prompt: recommendation.aiPrompt,
        ai_response: recommendation.aiResponse
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  // 获取用户推荐历史
  async getUserRecommendations(userId, limit = 50) {
    if (!userId) return []

    const { data, error } = await supabase
      .from(TABLES.RECOMMENDATIONS)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  },

  // 根据心情获取历史推荐
  async getRecommendationsByMood(userId, mood, limit = 10) {
    if (!userId) return []

    const { data, error } = await supabase
      .from(TABLES.RECOMMENDATIONS)
      .select('*')
      .eq('user_id', userId)
      .eq('mood', mood)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }
}

// 心情会话服务
export const moodSessionService = {
  // 创建新的心情会话
  async createMoodSession(userId, sessionData) {
    const { data, error } = await supabase
      .from(TABLES.MOOD_SESSIONS)
      .insert({
        user_id: userId,
        mood: sessionData.mood,
        weather: sessionData.weather,
        location: sessionData.location,
        selected_tracks: sessionData.selectedTracks || []
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  // 更新心情会话
  async updateMoodSession(sessionId, updates) {
    const { data, error } = await supabase
      .from(TABLES.MOOD_SESSIONS)
      .update({
        ...updates,
        ended_at: updates.ended ? new Date().toISOString() : undefined
      })
      .eq('id', sessionId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // 获取用户的心情会话历史
  async getUserMoodSessions(userId, limit = 20) {
    if (!userId) return []

    const { data, error } = await supabase
      .from(TABLES.MOOD_SESSIONS)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  },

  // 获取心情统计
  async getMoodStats(userId, days = 30) {
    if (!userId) return {}

    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from(TABLES.MOOD_SESSIONS)
      .select('mood, created_at')
      .eq('user_id', userId)
      .gte('created_at', startDate.toISOString())

    if (error) throw error

    // 统计心情频率
    const moodCounts = {}
    data?.forEach(session => {
      moodCounts[session.mood] = (moodCounts[session.mood] || 0) + 1
    })

    return {
      totalSessions: data?.length || 0,
      moodCounts,
      period: days
    }
  }
}

// 通用数据库操作
export const dbService = {
  // 获取数据库统计信息
  async getStats() {
    const [tracksResult, favoritesResult, recommendationsResult, sessionsResult] = await Promise.allSettled([
      supabase.from(TABLES.TRACKS).select('id', { count: 'exact', head: true }),
      supabase.from(TABLES.USER_FAVORITES).select('id', { count: 'exact', head: true }),
      supabase.from(TABLES.RECOMMENDATIONS).select('id', { count: 'exact', head: true }),
      supabase.from(TABLES.MOOD_SESSIONS).select('id', { count: 'exact', head: true })
    ])

    return {
      tracksCount: tracksResult.status === 'fulfilled' ? tracksResult.value.count : 0,
      favoritesCount: favoritesResult.status === 'fulfilled' ? favoritesResult.value.count : 0,
      recommendationsCount: recommendationsResult.status === 'fulfilled' ? recommendationsResult.value.count : 0,
      sessionsCount: sessionsResult.status === 'fulfilled' ? sessionsResult.value.count : 0
    }
  }
}

export default {
  userService,
  trackService,
  favoriteService,
  recommendationService,
  moodSessionService,
  dbService
}
